import { useCallback, useEffect, useRef, useState } from 'react';
import { LngLatBounds, Map as MLMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { circleRing, insertIndex, joinStroke, ringSelfIntersects } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { rasterStyle } from '../lib/mapStyle';

type LngLat = [number, number];
type MultiPolygon = LngLat[][][];

interface Boundary {
  id: string;
  code: string;
  name: string;
  isRemote: boolean;
  boundary: MultiPolygon;
}

const DRAFT = 'zone-draft';
const OTHERS = 'zone-others';
const BLUE = '#2563eb';
const RED = '#dc2626';

/** 画圆可选的半径(米) */
const RADII = [300, 500, 1000, 2000, 3000, 5000];
const radiusText = (m: number) => (m >= 1000 ? `${m / 1000} 公里` : `${m} 米`);

/**
 * FREEHAND 手绘:按住沿边界描,可以分好几笔(描一段、挪地图、接着描),绕回起点或点「画好了」就成形
 * POINTS 点边界 / CIRCLE 画圆
 */
type Mode = 'FREEHAND' | 'POINTS' | 'CIRCLE';

/**
 * 在地图上画一个区的范围:沿着边界依次点,连成一个圈。
 *
 * ★ 订单有坐标、又落在这个圈里,判区就以圈为准(不看地址里写了什么词)——
 *   专治「地址里带着偏远区的词、其实离得很近」。价钱照旧在计价规则里按区设。
 *
 * ★ 别的区已经画好的范围用灰色铺在底下:画重了的话,两个圈都包着的地方
 *   按面积小的那个算,而画的人得知道自己是不是在别人的圈里画。
 */
export function ZoneBoundaryEditor({
  zone,
  onClose,
  onSaved,
}: {
  zone: { id: string; code: string; name: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const vertexMarkers = useRef<Marker[]>([]);
  /** 每条边中间的小圆点:拖它 / 点它就在这条边上插一个点 */
  const midMarkers = useRef<Marker[]>([]);
  const [points, setPoints] = useState<LngLat[]>([]);
  /** 撤销用:每一步改动之前的样子(加点、拖点、删点、画圆都算一步) */
  const [past, setPast] = useState<LngLat[][]>([]);
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const [mode, setMode] = useState<Mode>('POINTS');
  const [radius, setRadius] = useState(1000);
  const [center, setCenter] = useState<LngLat | null>(null);
  /** 地图的 click 只注册一次,读的是这两个 ref 里的当前值 */
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const radiusRef = useRef(radius);
  radiusRef.current = radius;
  const crossing = ringSelfIntersects(points);
  /** 手绘还没收口的那几笔(经纬度,每笔已精简);空 = 没在描 */
  const [trace, setTrace] = useState<LngLat[]>([]);
  const traceRef = useRef(trace);
  traceRef.current = trace;
  /** 手绘时撤销一笔用 */
  const [traceHist, setTraceHist] = useState<LngLat[][]>([]);
  /** 手绘时拖动 = 挪地图(按钮切换,或者按住空格) */
  const [panLock, setPanLock] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const panning = panLock || spaceHeld;
  const panRef = useRef(panning);
  panRef.current = panning;

  /** 所有改动都走这里,好让「撤销」一步一步退回去 */
  const change = useCallback((next: LngLat[]) => {
    setPast((h) => [...h.slice(-50), pointsRef.current]);
    setPoints(next);
  }, []);
  /** 手绘收口:描好的那一圈变成范围(替换掉原来的),回到「点边界」好拖点微调 */
  const finishTrace = useCallback(
    (t: LngLat[]) => {
      if (t.length >= 3) change(t);
      else setPoints((ps) => [...ps]); // 不成形就把原来的画回来
      setTrace([]);
      setTraceHist([]);
      setPanLock(false);
      setMode('POINTS');
    },
    [change],
  );
  const [hadBoundary, setHadBoundary] = useState(false);
  const [others, setOthers] = useState<Boundary[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // 地图 + 已有的范围
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const map = new MLMap({ container: ref.current, style: rasterStyle(dark), center: [174.762, -36.848], zoom: 11 });
    mapRef.current = map;
    map.doubleClickZoom.disable(); // 双击常被当成「点完了」,别让它顺手把地图放大

    map.on('load', async () => {
      map.addSource(OTHERS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: `${OTHERS}-fill`, type: 'fill', source: OTHERS, paint: { 'fill-color': '#64748b', 'fill-opacity': 0.12 } });
      map.addLayer({ id: `${OTHERS}-line`, type: 'line', source: OTHERS, paint: { 'line-color': '#64748b', 'line-width': 1.5, 'line-dasharray': [2, 2] } });
      map.addSource(DRAFT, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: `${DRAFT}-fill`, type: 'fill', source: DRAFT, filter: ['==', '$type', 'Polygon'], paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.2 } });
      map.addLayer({ id: `${DRAFT}-line`, type: 'line', source: DRAFT, filter: ['!has', 'closing'], paint: { 'line-color': '#2563eb', 'line-width': 2.5 } });
      // 最后一点连回第一点的那条边:保存时会自动补上,画成虚线 —— 不然看着像「乱连了一条线」
      map.addLayer({ id: `${DRAFT}-closing`, type: 'line', source: DRAFT, filter: ['has', 'closing'], paint: { 'line-color': '#2563eb', 'line-width': 2, 'line-dasharray': [2, 2] } });

      try {
        const all = await api<Boundary[]>('/zones/boundaries');
        const mine = all.find((b) => b.id === zone.id);
        setOthers(all.filter((b) => b.id !== zone.id));
        if (mine) {
          const outer = mine.boundary[0]?.[0] ?? [];
          // 存的环首尾是同一个点,画的时候不要重复那一个
          // 读回来的是起点,不进撤销记录
          setPoints(outer.slice(0, -1));
          setHadBoundary(true);
        }
        // 视角:先对准这个区自己的圈,没有的话对准所有已画的圈
        const fit = mine ? [mine] : all;
        const coords = fit.flatMap((b) => b.boundary.flat(2));
        if (coords.length > 0) {
          const bounds = coords.reduce((bb, c) => bb.extend(c), new LngLatBounds(coords[0], coords[0]));
          map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 0 });
        }
      } catch (e) {
        setErr(e instanceof ApiFailure ? e.message : '读取已有范围失败');
      }
      setReady(true);
    });

    /*
     * ★ 手绘。按下时关掉地图拖动,沿途采屏幕坐标;松开后按 4 像素精简 ——
     *   描一圈几百个点,留下的只有拐弯处,每个都还能拖。
     *
     * ★ 可以分好几笔:想画准要放大,放大了一屏只装得下一段边界,
     *   所以松手只是「这一笔完了」—— 挪一下地图(「挪地图」/ 按住空格 / 两根手指)接着描,
     *   接头不用对得很准(见 joinStroke)。描回起点附近,或者点「画好了」,才成形。
     */
    const SNAP = 24; // 接头、收口认多远以内(像素)
    let stroke: Array<[number, number]> | null = null;
    const toLngLat = ([x, y]: [number, number]): LngLat => {
      const ll = map.unproject([x, y]);
      return [ll.lng, ll.lat];
    };
    const toPx = ([lng, lat]: LngLat): [number, number] => {
      const p = map.project([lng, lat]);
      return [p.x, p.y];
    };
    const preview = (live: LngLat[]) => {
      const t = traceRef.current;
      const line = [...t, ...live];
      (map.getSource(DRAFT) as { setData?: (d: unknown) => void } | undefined)?.setData?.({
        type: 'FeatureCollection',
        features: [
          { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line } },
          ...(line.length >= 2
            ? [{ type: 'Feature', properties: { closing: true }, geometry: { type: 'LineString', coordinates: [line[line.length - 1]!, line[0]!] } }]
            : []),
        ],
      });
    };
    const abort = () => {
      if (!stroke) return;
      stroke = null;
      map.dragPan.enable();
      preview([]);
    };
    const begin = (e: { point: { x: number; y: number }; points?: unknown[]; originalEvent: Event; preventDefault: () => void }) => {
      if (modeRef.current !== 'FREEHAND' || panRef.current) return;
      if (e.points && e.points.length > 1) return abort(); // 两根手指是在挪 / 缩放地图
      if ((e.originalEvent as MouseEvent).button > 0) return;
      if ((e.originalEvent.target as HTMLElement | null)?.closest('.maplibregl-marker')) return;
      e.preventDefault();
      map.dragPan.disable();
      stroke = [[e.point.x, e.point.y]];
    };
    const move = (e: { point: { x: number; y: number }; points?: unknown[] }) => {
      if (!stroke) return;
      if (e.points && e.points.length > 1) return abort();
      stroke.push([e.point.x, e.point.y]);
      preview(stroke.map(toLngLat));
    };
    const end = () => {
      if (!stroke) return;
      const s = stroke;
      stroke = null;
      map.dragPan.enable();
      const r = joinStroke(traceRef.current.map(toPx), s, { tolerance: 4, snap: SNAP });
      if (r.ignored) return preview([]);
      const geo = r.path.map(toLngLat);
      if (r.closed) return finishTrace(geo);
      // ★ 先取出来:更新函数是下一次渲染时才跑,那时 traceRef 已经是新的那段了
      const before = traceRef.current;
      setTraceHist((h) => [...h, before]);
      setTrace(geo);
    };
    map.on('mousedown', begin);
    map.on('touchstart', begin);
    map.on('mousemove', move);
    map.on('touchmove', move);
    map.on('mouseup', end);
    map.on('touchend', end);
    window.addEventListener('mouseup', end); // 拖出地图外松手

    map.on('click', (e) => {
      if (modeRef.current === 'FREEHAND') return; // 手绘松手后浏览器补的那一下 click
      // ★ 点在已有的点 / 边中点上不算「加一个点」—— 圆点的拖动走的是地图的鼠标事件,点它也会冒到这里
      if ((e.originalEvent.target as HTMLElement | null)?.closest('.maplibregl-marker')) return;
      const p: LngLat = [e.lngLat.lng, e.lngLat.lat];
      if (modeRef.current === 'CIRCLE') {
        setCenter(p);
        change(circleRing(p, radiusRef.current));
        return;
      }
      // ★ 不一律接在最后:插到最近的那条边上,回头补点也不会画出「8」字
      const ps = pointsRef.current;
      const i = insertIndex(ps, p);
      change([...ps.slice(0, i), p, ...ps.slice(i)]);
    });

    // ★ 刚点过的按钮(「手绘」「挪地图」)还带着焦点,空格默认会再按它一下 —— 手绘时空格只管挪地图
    const isTyping = (e: KeyboardEvent) => (e.target as HTMLElement | null)?.closest('input, select, textarea');
    const keydown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || isTyping(e) || modeRef.current !== 'FREEHAND') return;
      e.preventDefault();
      setSpaceHeld(true);
    };
    const keyup = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      if (!isTyping(e) && modeRef.current === 'FREEHAND') e.preventDefault();
      setSpaceHeld(false);
    };
    window.addEventListener('keydown', keydown);
    window.addEventListener('keyup', keyup);

    return () => {
      window.removeEventListener('keydown', keydown);
      window.removeEventListener('keyup', keyup);
      window.removeEventListener('mouseup', end);
      vertexMarkers.current.forEach((m) => m.remove());
      midMarkers.current.forEach((m) => m.remove());
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 别的区的范围
  useEffect(() => {
    const src = mapRef.current?.getSource(OTHERS) as { setData?: (d: unknown) => void } | undefined;
    src?.setData?.({
      type: 'FeatureCollection',
      features: others.map((b) => ({ type: 'Feature', properties: {}, geometry: { type: 'MultiPolygon', coordinates: b.boundary } })),
    });
  }, [others, ready]);

  // 正在画的圈 + 每个点一个可拖的小圆点
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (trace.length > 0) {
      // 手绘还没收口:只画描好的那几笔(实线)和自动连回起点的虚线,原来的点先收起来
      for (const id of [`${DRAFT}-fill`, `${DRAFT}-line`, `${DRAFT}-closing`]) {
        if (map.getLayer(id)) map.setPaintProperty(id, id.endsWith('fill') ? 'fill-color' : 'line-color', BLUE);
      }
      (map.getSource(DRAFT) as { setData?: (d: unknown) => void } | undefined)?.setData?.({
        type: 'FeatureCollection',
        features: [
          { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: trace } },
          { type: 'Feature', properties: { closing: true }, geometry: { type: 'LineString', coordinates: [trace[trace.length - 1]!, trace[0]!] } },
        ],
      });
      vertexMarkers.current.forEach((m) => m.remove());
      midMarkers.current.forEach((m) => m.remove());
      vertexMarkers.current = [];
      midMarkers.current = [];
      return;
    }
    const closed = points.length >= 3;
    // 边交叉了整圈标红 —— 存进去服务端也会拒,画的时候就让人看见
    const color = crossing ? RED : BLUE;
    for (const id of [`${DRAFT}-fill`, `${DRAFT}-line`, `${DRAFT}-closing`]) {
      if (map.getLayer(id)) map.setPaintProperty(id, id.endsWith('fill') ? 'fill-color' : 'line-color', color);
    }
    (map.getSource(DRAFT) as { setData?: (d: unknown) => void } | undefined)?.setData?.({
      type: 'FeatureCollection',
      features: [
        // 自己点出来的边:实线
        { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: points } },
        ...(closed
          ? [
              { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[...points, points[0]!]] } },
              // 自动闭合的那条边:虚线
              {
                type: 'Feature',
                properties: { closing: true },
                geometry: { type: 'LineString', coordinates: [points[points.length - 1]!, points[0]!] },
              },
            ]
          : []),
      ],
    });

    vertexMarkers.current.forEach((m) => m.remove());
    vertexMarkers.current = points.map((p, i) => {
      const el = document.createElement('div');
      el.className = 'h-3.5 w-3.5 rounded-full border-2 border-white bg-brand-600 shadow cursor-move';
      el.setAttribute('aria-label', `范围上的第 ${i + 1} 个点`);
      el.title = '拖动调整位置;双击删掉这个点';
      el.addEventListener('dblclick', (ev) => {
        ev.stopPropagation();
        change(pointsRef.current.filter((_, j) => j !== i));
      });
      const m = new Marker({ element: el, draggable: true }).setLngLat(p).addTo(map);
      m.on('dragend', () => {
        const ll = m.getLngLat();
        change(pointsRef.current.map((q, j) => (j === i ? [ll.lng, ll.lat] : q)));
      });
      return m;
    });

    /*
     * ★ 每条边中间一个小空心点:拖它(或点一下)就在**这条边上**插一个点。
     *   不然新点只能接在最后,改一个已经画好的范围时,
     *   想在中间某条边上补个拐角根本做不到。
     */
    midMarkers.current.forEach((m) => m.remove());
    const edges = closed ? points.length : points.length - 1;
    midMarkers.current = Array.from({ length: Math.max(0, edges) }, (_, i) => {
      const a = points[i]!;
      const b = points[(i + 1) % points.length]!;
      const mid: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const el = document.createElement('div');
      el.className = 'h-3 w-3 rounded-full border-2 border-brand-600 bg-white/80 shadow cursor-copy';
      el.setAttribute('aria-label', `在第 ${i + 1} 条边上加点`);
      el.title = '拖动或点一下,在这条边上加一个点';
      const insert = (p: LngLat) => {
        const ps = pointsRef.current;
        change([...ps.slice(0, i + 1), p, ...ps.slice(i + 1)]);
      };
      // 拖完松手浏览器还会补一个 click —— 拖过就不再按「点一下」插一次
      let dragged = false;
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (dragged) return;
        insert(mid);
      });
      const m = new Marker({ element: el, draggable: true }).setLngLat(mid).addTo(map);
      m.on('dragstart', () => {
        dragged = true;
      });
      m.on('dragend', () => {
        const ll = m.getLngLat();
        insert([ll.lng, ll.lat]);
      });
      return m;
    });
  }, [points, trace, ready, crossing, change]);

  useEffect(() => {
    const c = mapRef.current?.getCanvas();
    if (c) c.style.cursor = mode === 'FREEHAND' && !panning ? 'crosshair' : '';
  }, [mode, panning, ready]);

  function undo() {
    if (trace.length > 0) {
      // 手绘还没收口时,撤销的是最后一笔
      setTrace(traceHist[traceHist.length - 1] ?? []);
      setTraceHist((h) => h.slice(0, -1));
      return;
    }
    const prev = past[past.length - 1];
    if (!prev) return;
    setPast((h) => h.slice(0, -1));
    setPoints(prev);
  }

  function pickRadius(m: number) {
    setRadius(m);
    // 已经点过圆心的话,换半径就按新半径重画
    if (mode === 'CIRCLE' && center) change(circleRing(center, m));
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/zones/${zone.id}/boundary`, { method: 'PUT', body: { points } });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '没存上');
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/zones/${zone.id}/boundary`, { method: 'DELETE' });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '没清掉');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-white dark:bg-slate-900" role="dialog" aria-modal="true" aria-label="画区域范围">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] dark:border-slate-700">
        <div className="mr-auto min-w-0">
          <div className="font-medium text-slate-900 dark:text-slate-100">
            画范围:{zone.code} {zone.name}
          </div>
          <div className="text-xs text-slate-500">
            {mode === 'FREEHAND'
              ? '按住鼠标(或手指)沿着边界描。可以分几笔:描一段,点「挪地图」(或按住空格、两根手指)挪一下,再接着描。描回起点附近或点「画好了」就成形(会替换掉现在画的),之后可以拖点微调。'
              : mode === 'CIRCLE'
                ? '点一下地图当圆心,按选的半径画一个圆;再点别处就换圆心。画好后可以切回「点边界」拖点微调。'
                : '先把地图挪到那一片,再点「手绘」描一圈最快;也可以沿着边界一个个点,新点会自动插到最近的那条边上。拖圆点调位置、双击圆点删掉;拖边中间的空心点在那条边上加点。'}
            灰色虚线是别的区已经画好的范围。
          </div>
        </div>
        <div role="radiogroup" aria-label="画法" className="flex overflow-hidden rounded-lg border border-slate-300 text-sm dark:border-slate-600">
          {(
            [
              ['FREEHAND', '手绘'],
              ['POINTS', '点边界'],
              ['CIRCLE', '画圆'],
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => {
                // 描了一半换画法:描好的那几笔先收口成形,别白描
                if (trace.length > 0) finishTrace(trace);
                setMode(m);
              }}
              className={`h-9 px-3 ${mode === m ? 'bg-brand-600 text-white' : 'bg-white dark:bg-slate-800'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {mode === 'CIRCLE' && (
          <label className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300">
            半径
            <select
              value={radius}
              onChange={(e) => pickRadius(Number(e.target.value))}
              className="h-9 rounded-lg border border-slate-300 bg-white px-2 dark:border-slate-600 dark:bg-slate-800"
            >
              {RADII.map((m) => (
                <option key={m} value={m}>
                  {radiusText(m)}
                </option>
              ))}
            </select>
          </label>
        )}
        {mode === 'FREEHAND' && (
          <button
            type="button"
            aria-pressed={panning}
            onClick={() => setPanLock((v) => !v)}
            title="打开时拖动是挪地图,不画线;也可以按住空格临时挪"
            className={`h-9 rounded-lg border px-3 text-sm ${
              panning ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300' : 'border-slate-300 dark:border-slate-600'
            }`}
          >
            挪地图
          </button>
        )}
        {trace.length > 0 && (
          <button
            type="button"
            onClick={() => finishTrace(trace)}
            disabled={trace.length < 3}
            className="h-9 rounded-lg border border-brand-600 px-3 text-sm text-brand-700 disabled:opacity-40 dark:text-brand-300"
          >
            画好了
          </button>
        )}
        <span className="text-sm tabular-nums text-slate-500" aria-live="polite">
          {trace.length > 0 ? `已描 ${traceHist.length} 笔 · ${trace.length} 个点` : `${points.length} 个点`}
        </span>
        <button type="button" onClick={undo} disabled={(past.length === 0 && trace.length === 0) || busy} className="h-9 rounded-lg border border-slate-300 px-3 text-sm disabled:opacity-40 dark:border-slate-600">
          撤销
        </button>
        <button
          type="button"
          onClick={() => {
            if (trace.length > 0) {
              setTrace([]);
              setTraceHist([]);
              return;
            }
            change([]);
            setCenter(null);
          }}
          disabled={(points.length === 0 && trace.length === 0) || busy}
          className="h-9 rounded-lg border border-slate-300 px-3 text-sm disabled:opacity-40 dark:border-slate-600"
        >
          清空重画
        </button>
        {hadBoundary && (
          <button type="button" onClick={() => void clear()} disabled={busy} className="h-9 rounded-lg border border-red-300 px-3 text-sm text-red-600 disabled:opacity-40">
            清除范围
          </button>
        )}
        <button type="button" onClick={() => void save()} disabled={points.length < 3 || crossing || trace.length > 0 || busy} className="h-9 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white disabled:opacity-40">
          {busy ? '保存中…' : '保存范围'}
        </button>
        <button type="button" onClick={onClose} disabled={busy} className="h-9 rounded-lg border border-slate-300 px-3 text-sm dark:border-slate-600">
          取消
        </button>
      </div>
      {crossing && (
        <p role="alert" className="bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          范围的边交叉了(标红的那圈),这样存不进去。拖动标红处的点把它理顺,或者点「撤销」退回上一步。
        </p>
      )}
      {err && <p className="bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{err}</p>}
      <div ref={ref} className="min-h-0 flex-1" aria-label="画范围的地图" />
    </div>
  );
}
